// with formik hook

import { useState, useEffect } from 'react';
import { Link, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { useFormik, Formik, Field, Form } from 'formik';
import { wait } from '../../ulits/wait';

import { userSchema } from '../../schemas';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { Loader } from '../Loader';
import { Snackbar } from '../Snackbar';
import { allCountries } from '../../data/countries';

import { getLocation, getCountryCodes } from '../../fetchApi';

// import 'bootstrap/dist/css/bootstrap.min.css';
import './Table.scss';
import './UserDetails.scss';

const EditUser = (props) => {
  // state hooks
  const { sendSnackbar } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [usersCoords, setUsersCoords] = useState(['0', '0']);
  const [error, setError] = useState(null);
  const [isGettingCoords, setIsGettingCoords] = useState(false);

  const navigate = useNavigate();
  const userId = useParams('id');

  // if (localStorage.getItem('users') === null) {
  //   console.error('localstorage item "users" is null');
  //   sendSnackbar('error', "Couldn't fetch user");
  //   navigate('/');
  //   return;
  // }

  const storedUsers = JSON.parse(window.localStorage.getItem('users'));

  const user = storedUsers.find(
    (currentUser) => currentUser.login.uuid === userId.id
  );

  useEffect(() => {
    setUsersCoords(
      user.realLocation.coords ? user.realLocation.coords : [0, 0]
    );
  }, []);

  const onSubmit = async (vals, actions) => {
    const curUserIndex = storedUsers.findIndex(
      (person) => person.login.uuid === userId.id
    );

    user.dob.age = vals.age;
    user.location.city = vals.city;
    user.location.country = vals.country;
    user.location.state = vals.state;
    user.realLocation.country = vals.country;
    user.dob.date = vals.dateOfBirth;
    user.email = vals.email;
    user.name.first = vals.firstName;
    user.gender = vals.gender;
    user.name.last = vals.lastName;
    user.picture.large = vals.picture;
    user.location.postcode = vals.postcode;
    user.location.street.name = vals.streetName;
    user.location.street.number = vals.streetNumber;
    user.name.title = vals.title;
    user.login.username = vals.username;

    user.realLocation.coords = usersCoords;

    storedUsers[curUserIndex] = user;

    window.localStorage.setItem('users', JSON.stringify(storedUsers));

    await wait(2000);
    sendSnackbar('success', 'User updated');
    navigate(-2);
  };

  // * formik setup stuff
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
  } = useFormik({
    initialValues: {
      //! ALL VALUES HERE
      username: user.login.username,
      firstName: user.name.first,
      lastName: user.name.last,
      title: user.name.title,
      email: user.email,
      gender: user.gender,
      age: user.dob.age,
      dateOfBirth: user.dob.date,
      country: user.realLocation.country,
      city: user.location.city,
      streetName: user.location.street.name,
      streetNumber: user.location.street.number,
      postcode: user.location.postcode,
      state: user.location.state,
      picture: user.picture.large,
      id: user.login.uuid,
      latitude: `${user.realLocation.coords ? user.realLocation.coords[0] : 0}`,
      longitude: `${
        user.realLocation.coords ? user.realLocation.coords[1] : 0
      }`,
    },
    validationSchema: userSchema,
    onSubmit,
  });

  const deleteUser = () => {
    const usersInStorage = JSON.parse(window.localStorage.getItem('users'));

    if (!usersInStorage) {
      sendSnackbar('error', "Couldn't fetch users");
      navigate('/');
    }
    const newUsers = usersInStorage.filter(
      (currentUser) => currentUser.login.uuid !== user.login.uuid
    );
    window.localStorage.setItem('users', JSON.stringify(newUsers));
    sendSnackbar('success', 'User deleted');
    navigate('/');
  };

  const handleRadioTitle = (e) => {
    values.title = e.target.value;
  };
  const handleRadioGender = (e) => {
    values.gender = e.target.value;
  };
  // randomize img based on actual title (mr or mrs)
  const changeImg = () => {
    const { gender } = values;
    const randomInt = Math.floor(Math.random() * 100);
    let sex = 'women';
    const imgToSwap = document.getElementById('chosenImg');

    if (gender.toLowerCase() === 'male') {
      sex = 'men';
    }
    const imgSrc = `https://randomuser.me/api/portraits/${sex}/${randomInt}.jpg`;
    values.picture = imgSrc;
    imgToSwap.src = imgSrc;
  };

  const handleCoordsChange = (e) => {
    if (e.target.id === 'latitude') {
      setUsersCoords([e.target.value, usersCoords[1]]);
    } else {
      setUsersCoords([usersCoords[0], e.target.value]);
    }
  };
  // randomize coords based on country code (if can't fetch country code, display error)
  const refreshLocation = async (country) => {
    if (!allCountries.includes(country)) {
      setError('Country name is invalid');
      sendSnackbar('error', 'Country name is invalid');
      return;
    }
    setIsGettingCoords(true);
    // check if country name is valid, if so, get its country code
    // * getCountryCodes accepts and returns array!
    const countryCode = getCountryCodes([country])[0];

    // we have code so we can fetch coords now
    const newCoords = await getLocation(countryCode);

    setUsersCoords(newCoords);
    setIsGettingCoords(false);
  };

  return (
    <div className="user-details">
      <h2>Edit User Page</h2>
      <div className="user-details-container">
        <form autoComplete="off" onSubmit={(e) => handleSubmit(e)}>
          <header className="user-header">
            <h3>
              {user.name.first} {user.name.last}
            </h3>
            <Button
              className="img-randomizable"
              type="button"
              onClick={changeImg}
              onKeyDown={changeImg}
            >
              <img id="chosenImg" src={values.picture} alt="" />
            </Button>
          </header>
          <Table striped bordered hover variant="dark">
            <thead>
              <tr>
                <th>Property</th>
                <th colSpan={3}>Value</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>Username</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.username}
                    id="username"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.username && touched.username ? 'input-error' : ''
                    }
                  />
                  {errors.username && touched.username ? (
                    <span className="error">{errors.username}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>First name</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.firstName}
                    id="firstName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.firstName && touched.firstName ? 'input-error' : ''
                    }
                  />
                  {errors.firstName && touched.firstName ? (
                    <span className="error">{errors.firstName}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Last name</td>
                <td className="inputTd">
                  <input
                    autoComplete="off"
                    type="text"
                    value={values.lastName}
                    id="lastName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.lastName && touched.lastName ? 'input-error' : ''
                    }
                  />
                  {errors.lastName && touched.lastName ? (
                    <span className="error">{errors.lastName}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Title</td>
                <td className="inputTd">
                  <fieldset>
                    <input
                      type="radio"
                      value="mr"
                      name="title"
                      id="mr"
                      onChange={(e) => handleRadioTitle(e)}
                      onBlur={handleBlur}
                      defaultChecked={
                        values.title.toLowerCase() === 'mr' ? 'checked' : ''
                      }
                      className={
                        errors.title && touched.title ? 'input-error' : ''
                      }
                    />
                    <label htmlFor="mr">Mr</label>

                    <input
                      type="radio"
                      value="mrs"
                      name="title"
                      id="mrs"
                      onChange={(e) => handleRadioTitle(e)}
                      onBlur={handleBlur}
                      defaultChecked={
                        values.title.toLowerCase() === 'mrs' ? 'checked' : ''
                      }
                      className={
                        errors.title && touched.title ? 'input-error' : ''
                      }
                    />
                    <label htmlFor="mrs">Mrs</label>
                  </fieldset>

                  {errors.title && touched.title ? (
                    <span className="error">{errors.title}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Email</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.email}
                    id="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.email && touched.email ? 'input-error' : ''
                    }
                  />
                  {errors.email && touched.email ? (
                    <span className="error">{errors.email}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Gender</td>
                <td className="inputTd">
                  <fieldset>
                    <input
                      type="radio"
                      value="male"
                      name="gender"
                      id="male"
                      onChange={(e) => handleRadioGender(e)}
                      onBlur={handleBlur}
                      defaultChecked={
                        values.gender.toLowerCase() === 'male' ? 'checked' : ''
                      }
                      className={
                        errors.gender && touched.gender ? 'input-error' : ''
                      }
                    />
                    <label htmlFor="male">Male</label>

                    <input
                      type="radio"
                      value="female"
                      name="gender"
                      id="female"
                      onChange={(e) => handleRadioGender(e)}
                      onBlur={handleBlur}
                      defaultChecked={
                        values.gender.toLowerCase() === 'female'
                          ? 'checked'
                          : ''
                      }
                      className={
                        errors.gender && touched.gender ? 'input-error' : ''
                      }
                    />
                    <label htmlFor="female">Female</label>
                  </fieldset>
                </td>
              </tr>
              <tr>
                <td>Birthdate</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.dateOfBirth.slice(0, 10)}
                    id="dateOfBirth"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.dateOfBirth && touched.dateOfBirth
                        ? 'input-error'
                        : ''
                    }
                  />
                  {errors.dateOfBirth && touched.dateOfBirth ? (
                    <span className="error">{errors.dateOfBirth}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Age</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.age}
                    id="age"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.age && touched.age ? 'input-error' : ''}
                  />
                  {errors.age && touched.age ? (
                    <span className="error">{errors.age}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              {/* // ! Address */}
              <tr>
                <td colSpan="2">
                  <b>Address</b>
                </td>
              </tr>
              <tr>
                <td>Street Name</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.streetName}
                    id="streetName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.streetName && touched.streetName
                        ? 'input-error'
                        : ''
                    }
                  />
                  {errors.streetName && touched.streetName ? (
                    <span className="error">{errors.streetName}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Street Number</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.streetNumber}
                    id="streetNumber"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.streetNumber && touched.streetNumber
                        ? 'input-error'
                        : ''
                    }
                  />
                  {errors.streetNumber && touched.streetNumber ? (
                    <span className="error">{errors.streetNumber}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>State</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.state}
                    id="state"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.state && touched.state ? 'input-error' : ''
                    }
                  />
                  {errors.state && touched.state ? (
                    <span className="error">{errors.state}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>

              <tr>
                <td>City</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.city}
                    id="city"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.city && touched.city ? 'input-error' : ''}
                  />
                  {errors.city && touched.city ? (
                    <span className="error">{errors.city}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Postcode</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.postcode}
                    id="postcode"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.postcode && touched.postcode ? 'input-error' : ''
                    }
                  />
                  {errors.postcode && touched.postcode && (
                    <span className="error">{errors.postcode}</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Country</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.country}
                    id="country"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.country && touched.country ? 'input-error' : ''
                    }
                  />
                  {errors.country && touched.country ? (
                    <span className="error">{errors.country}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan="2">
                  <div className="geolocation">
                    <b>Geolocation (based on country)</b>
                    {isGettingCoords ? (
                      <Loader msg="" />
                    ) : (
                      <Button onClick={() => refreshLocation(values.country)}>
                        🔄
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
              <tr>
                <td>Latitude</td>
                <td className="inputTd">
                  <input
                    type="text"
                    id="latitude"
                    value={usersCoords[0]}
                    onChange={handleCoordsChange}
                    onBlur={handleBlur}
                    className={
                      errors.postcode && touched.postcode ? 'input-error' : ''
                    }
                  />
                </td>
              </tr>
              <tr>
                <td>Longitude</td>
                <td className="inputTd">
                  <input
                    type="text"
                    id="longitude"
                    value={usersCoords[1]}
                    onChange={handleCoordsChange}
                    onBlur={handleBlur}
                    className={
                      errors.postcode && touched.postcode ? 'input-error' : ''
                    }
                  />
                </td>
              </tr>
              <tr />
            </tbody>
          </Table>
          {modalOpen ? (
            <Modal
              closeModal={() => setModalOpen(false)}
              deleteUser={deleteUser}
            />
          ) : (
            ''
          )}

          {isSubmitting ? (
            <Loader msg="Updating Data" />
          ) : (
            <div className="edit-user-buttons">
              <Button theme="dark" type="button" onClick={() => navigate(-2)}>
                Discard changes
              </Button>
              <Button theme="dark" type="submit">
                Apply changes
              </Button>
              <Button
                theme="dark"
                type="button"
                onClick={() => setModalOpen(true)}
              >
                Delete User
              </Button>
            </div>
          )}

          {props.snackbar.visible && (
            <Snackbar type={props.snackbar.type} msg={props.snackbar.msg} />
          )}
        </form>
      </div>
    </div>
  );
};

export default EditUser;
